import React from "react";
import { useTranslation } from "react-i18next";
import { useYouTube } from "../../contexts/YouTubeContext";

const CHANNEL_ID = process.env.REACT_APP_YOUTUBE_CHANNEL_ID;
const YOUTUBE_CHANNEL_URL = CHANNEL_ID
  ? `https://www.youtube.com/channel/${CHANNEL_ID}`
  : "https://www.youtube.com";

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────
const YoutubeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-7 h-7 inline-block mr-2" fill="#FF0000">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const PlayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-14 h-14 drop-shadow-lg" fill="white">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const LiveBadge = ({ label }) => (
  <span className="inline-flex items-center gap-1.5 bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
    <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
    {label}
  </span>
);

const SkeletonCard = () => (
  <div className="border-2 border-cards-border rounded-[40px] bg-white shadow-lg overflow-hidden flex flex-col h-full animate-pulse">
    <div className="w-full aspect-video bg-gray-200" />
    <div className="p-5 flex flex-col gap-3">
      <div className="h-5 bg-gray-200 rounded w-3/4" />
      <div className="h-4 bg-gray-200 rounded w-full" />
      <div className="h-4 bg-gray-200 rounded w-5/6" />
    </div>
  </div>
);

const VideoCard = ({ video, t }) => {
  const videoUrl = `https://www.youtube.com/watch?v=${video.id}`;

  return (
    <div className="border-2 border-cards-border rounded-[40px] bg-white shadow-lg overflow-hidden flex flex-col h-full transition-transform duration-200 hover:scale-[1.02]">
      <a href={videoUrl} target="_blank" rel="noopener noreferrer"
        className="relative block w-full aspect-video bg-gradient-to-br from-[#625480] to-[#2d465e] flex-shrink-0 overflow-hidden">
        {video.thumbnail ? (
          <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <YoutubeIcon />
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors">
          <PlayIcon />
        </div>
        {video.isLive && (
          <div className="absolute top-3 left-3">
            <LiveBadge label={t("liveNow")} />
          </div>
        )}
      </a>

      <div className="flex flex-col flex-1 p-5 gap-3">
        <div className="flex items-start gap-2">
          {video.isLive && <span className="shrink-0 mt-1"><LiveBadge label={t("liveNow")} /></span>}
          <h3 className="font-poppins text-[18px] text-text font-semibold leading-snug">
            {video.title}
          </h3>
        </div>
        <p className="font-poppins text-text text-[14px] line-clamp-3 flex-1">
          {video.description}
        </p>
        <a href={videoUrl} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 font-poppins text-[14px] text-[#625480] font-semibold hover:underline mt-auto">
          {video.isLive ? t("joinLive") : t("watchOnYouTube")}
          <span>→</span>
        </a>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const YoutubeSection = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language || "en";
  const { videos, loading } = useYouTube();

  const showComingSoon = !loading && videos.length === 0;

  if (showComingSoon) {
    return (
      <div className="w-full mx-auto text-center mb-20 px-4">
        <h2 className="font-roboto text-[32px] text-text pt-10">
          <YoutubeIcon />
          {t("youtubeSection")}
        </h2>
        <p className="font-poppins text-[20px] italic text-text mb-8">
          {t("youtubeSectionSubtitle")}
        </p>
        <div className="py-16">
          <p className="font-poppins text-[24px] text-text mb-8">
            {language === "tr" ? "Çok Yakında" : "Coming Soon"}
          </p>
        </div>
        <div className="mt-10">
          <a href={YOUTUBE_CHANNEL_URL} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-button-bg text-white font-poppins font-semibold text-[16px] px-8 py-3 rounded-full shadow-md hover:opacity-90 transition-opacity">
            <YoutubeIcon />
            {t("visitOurChannel")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto text-center mb-20 px-4">
      <h2 className="font-roboto text-[32px] text-text pt-10">
        <YoutubeIcon />
        {t("youtubeSection")}
      </h2>
      <p className="font-poppins text-[20px] italic text-text mb-8">
        {t("youtubeSectionSubtitle")}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {loading
          ? [1, 2, 3].map((i) => <SkeletonCard key={i} />)
          : videos.map((video) => (
              <VideoCard key={video.id} video={video} t={t} />
            ))}
      </div>

      <div className="mt-10">
        <a href={YOUTUBE_CHANNEL_URL} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-button-bg text-white font-poppins font-semibold text-[16px] px-8 py-3 rounded-full shadow-md hover:opacity-90 transition-opacity">
          <YoutubeIcon />
          {t("visitOurChannel")}
        </a>
      </div>
    </div>
  );
};

export default YoutubeSection;
